declare global {
  interface Window {
    dataLayer: Record<string, any>[]
    gtag: (...args: any[]) => void
  }
}

// Base interfaces
export interface DataLayerEvent {
  event: string
  [key: string]: any
}

// Standardized base interface for all guest portal events
export interface BaseGuestPortalEvent {
  event: string
  timestamp: string
  session_id: string
  visitor_id: string
  user_id?: string
  page_path: string
  page_url: string
}

export interface VisitorData {
  visitorId: string
  firstVisit: string
  lastVisit: string
  visitCount: number
  sessionId: string
  sessionStart: string
}

// Standardized event categories
export const EVENT_CATEGORIES = {
  NAVIGATION: 'navigation',
  INTERACTION: 'interaction',
  ERROR: 'error',
  PERFORMANCE: 'performance',
  USER_JOURNEY: 'user_journey',
  SESSION: 'session'
} as const

// Standardized event names with consistent structure
export const STANDARD_EVENTS = {
  // Navigation events
  PAGE_VIEW: 'guest_portal_page_view',
  NAVIGATION: 'guest_portal_navigation',

  // Interaction events
  CLICK: 'guest_portal_click',
  FORM_SUBMISSION: 'guest_portal_form_submission',

  // User journey events
  USER_JOURNEY: 'guest_portal_user_journey',
  FIRST_TIME_USER_JOURNEY: 'guest_portal_first_time_user_journey',
  EXIT_INTENT: 'guest_portal_exit_intent',

  // Session events
  SESSION_DURATION: 'guest_portal_session_duration',

  // Error events
  LOGIN_ERROR: 'guest_portal_login_error',
  SSO_FAILURE: 'guest_portal_sso_failure',

  // Feature-specific events
  PROFILE_EDIT: 'guest_portal_profile_edit',
  BOOKING_MANAGEMENT: 'guest_portal_booking_management',
  PAYMENT_UPDATE: 'guest_portal_payment_update',
  CHECKIN: 'guest_portal_checkin'
} as const

// Specific event interfaces
export interface StandardPageViewEvent extends BaseGuestPortalEvent {
  event: typeof STANDARD_EVENTS.PAGE_VIEW
  visitor_type: 'new' | 'returning'
}

export interface StandardNavigationEvent extends BaseGuestPortalEvent {
  event: typeof STANDARD_EVENTS.NAVIGATION
  from_page: string
  to_page: string
  navigation_type:
    | 'link_click'
    | 'back_button'
    | 'forward_button'
    | 'browser_navigation'
    | 'programmatic'
}

export interface StandardClickEvent extends BaseGuestPortalEvent {
  event: typeof STANDARD_EVENTS.CLICK
  click_element: string
  element_type: string
  destination_url?: string
  click_context?: string
}

export interface StandardUserJourneyEvent extends BaseGuestPortalEvent {
  event: typeof STANDARD_EVENTS.USER_JOURNEY
  journey_path: string[]
  total_pages: number
  session_duration_minutes: number
}

export interface StandardExitIntentEvent extends BaseGuestPortalEvent {
  event: typeof STANDARD_EVENTS.EXIT_INTENT
  exit_page: string
  time_on_page_seconds: number
  exit_type:
    | 'tab_close'
    | 'navigate_away'
    | 'back_button'
    | 'timeout'
    | 'page_refresh'
}

export interface StandardSessionDurationEvent extends BaseGuestPortalEvent {
  event: typeof STANDARD_EVENTS.SESSION_DURATION
  session_duration_seconds: number
}

export interface StandardFormEvent extends BaseGuestPortalEvent {
  event: typeof STANDARD_EVENTS.FORM_SUBMISSION
  form_type: string
  form_action: 'submit' | 'abandon'
  field_name?: string
  error_type?: string
  error_message?: string
}

export interface StandardErrorEvent extends BaseGuestPortalEvent {
  event: typeof STANDARD_EVENTS.LOGIN_ERROR | typeof STANDARD_EVENTS.SSO_FAILURE
  error_category: string
  error_type: string
  error_message?: string
  error_code?: string
  error_context?: Record<string, any>
}

// Helper types
export interface CachedPageInfo {
  pathname: string
  title: string
  href: string
  timestamp: number
}

export interface PageSessionData {
  startTime: number
  pagePath: string
  pageTitle: string
  userId?: string
}

// Legacy FunnelSessionData interface removed - now using unified first-time user journey tracking
